'use client';
import posthog from 'posthog-js';
import type { EventMap, EventName, Surface } from './events';
import { sanitizePath, sanitizeUrl } from './url';

/**
 * Browser-side PostHog. Every rule here exists for a reason written down in
 * docs/investigations/posthog-analytics.md:
 *
 * - autocapture OFF: it ships the TEXT of the clicked element, and on
 *   /app/professional/patients/[id] that text is a patient's name.
 * - session recording OFF: one mis-set mask on a health platform records a
 *   consultation note. Turning it on is a product decision, not a default.
 * - manual pageviews: the App Router does not reload between routes, so the
 *   library's automatic capture would fire once per hard load and never again.
 * - api_host is OUR origin (/ingest): an adblocker eating posthog.com requests
 *   does not just lose events, it biases the funnel toward less technical users.
 *
 * With no key configured (local dev, previews) every function here is a no-op,
 * which is deliberate: our own clicking must never enter the production funnels.
 */

const KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY;
const HOST = process.env.NEXT_PUBLIC_POSTHOG_HOST || '/ingest';
const UI_HOST = process.env.NEXT_PUBLIC_POSTHOG_UI_HOST || 'https://us.posthog.com';

let started = false;
/** Set while an admin is impersonating: nothing is sent at all. */
let muted = false;

export function analyticsEnabled(): boolean {
  return Boolean(KEY) && started && !muted;
}

export function initAnalytics(): void {
  if (started || !KEY || typeof window === 'undefined') return;
  started = true;

  posthog.init(KEY, {
    api_host: HOST,
    ui_host: UI_HOST,
    autocapture: false,
    disable_session_recording: true,
    // `autocapture: false` does NOT cover these — they are separate features, and
    // PostHog turns them ON by default for a new project (found live in prod
    // 2026-08-27, emitting $dead_click and $exception). Pinned here as well as in
    // the project settings so a toggle in the PostHog UI cannot silently switch
    // them back on and quietly contradict the Privacy Policy.
    //
    // The dangerous one is exceptions: an error message is arbitrary text and can
    // carry an id, a URL with a token, or an API error body. Performance capture
    // records resource URLs, which is the same problem by another route.
    capture_dead_clicks: false,
    capture_exceptions: false,
    capture_heatmaps: false,
    capture_performance: false,
    disable_surveys: true,
    capture_pageview: false,
    capture_pageleave: true,
    // Anonymous visitors still emit events (the guest booking funnel starts
    // anonymous), they just do not each get a stored person profile.
    person_profiles: 'identified_only',
    // Last line of defence: even if a call site passes a raw URL, it is scrubbed
    // here before anything leaves the browser.
    sanitize_properties: (properties) => {
      const out = { ...properties };
      if (typeof out.$current_url === 'string') out.$current_url = sanitizeUrl(out.$current_url);
      if (typeof out.$pathname === 'string') out.$pathname = sanitizePath(out.$pathname);
      if (typeof out.$referrer === 'string' && out.$referrer !== '$direct') {
        out.$referrer = sanitizeUrl(out.$referrer);
      }
      delete out.$initial_current_url;
      return out;
    },
  });
}

/** Properties attached to every subsequent event. */
export function registerSuperProperties(props: {
  surface?: Surface;
  role?: string;
  locale?: string;
  market?: string;
  app_version?: string;
}): void {
  if (!KEY || !started) return;
  posthog.register(props);
}

/**
 * Ties the anonymous journey to the account. Called on login, on signup and on
 * first load of any authenticated shell — `identify` is idempotent, and calling
 * it late is what keeps "visited profile -> paid" as ONE funnel instead of two.
 */
export function identifyUser(
  userId: string,
  properties: Record<string, unknown>,
  setOnce?: Record<string, unknown>,
): void {
  if (!KEY || !started || muted) return;
  posthog.identify(userId, properties, setOnce);
}

export function setGroup(type: 'professional' | 'market' | 'team', key: string): void {
  if (!analyticsEnabled()) return;
  posthog.group(type, key);
}

/** Logout. Without this the next person on the same browser inherits an identity. */
export function resetAnalytics(): void {
  if (!KEY || !started) return;
  posthog.reset();
}

/**
 * Admin acting as another user. Their clicks are support work, not product
 * usage, so we drop the events entirely rather than tagging them and hoping
 * every future insight remembers to filter the tag out.
 */
export function muteAnalytics(isImpersonating: boolean): void {
  muted = isImpersonating;
}

/**
 * The user's LGPD objection, applied to the browser half. Uses PostHog's own
 * opt-out (not just our `muted` flag) so the library also stops writing its
 * cookie and clears the stored id — objecting to measurement and still carrying
 * the tracker's cookie would be a half-measure.
 *
 * Idempotent: called on every mount of the identity component.
 */
export function setAnalyticsOptOut(optedOut: boolean): void {
  if (!KEY || !started) return;
  if (optedOut) {
    if (!posthog.has_opted_out_capturing()) posthog.opt_out_capturing();
    return;
  }
  if (posthog.has_opted_out_capturing()) posthog.opt_in_capturing();
}

/** The only way to emit an event. `name` must exist in the registry. */
export function capture<K extends EventName>(name: K, properties: EventMap[K]): void {
  if (!analyticsEnabled()) return;
  posthog.capture(name, properties as Record<string, unknown>);
}

/** Manual pageview for the App Router. */
export function capturePageview(url: string): void {
  if (!analyticsEnabled()) return;
  posthog.capture('$pageview', { $current_url: sanitizeUrl(url) });
}
